import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

// Mobile breakpoint (matches Tailwind's lg)
const MOBILE_BREAKPOINT = 1024;

export interface SpotlightStep {
  id: string;
  target: string;
  title: string;
  description: string;
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'auto';
  route?: string;
  action?: 'click' | 'hover' | 'none';
  waitFor?: string;
  onBeforeShow?: () => void;
  onAfterShow?: () => void;
}

interface OnboardingSpotlightProps {
  isOpen: boolean;
  onClose: () => void;
  steps: SpotlightStep[];
  startAtStep?: number;
}

interface DisplayState {
  stepIndex: number;
  step: SpotlightStep;
  targetRect: DOMRect | null;
  tooltipStyle: React.CSSProperties;
}

const PADDING = 12;
const TOOLTIP_MARGIN = 20;

export default function OnboardingSpotlight({
  isOpen,
  onClose,
  steps,
  startAtStep = 0,
}: OnboardingSpotlightProps) {
  const { t } = useTranslation('onboarding');
  const navigate = useNavigate();
  const location = useLocation();

  // Single state object to keep everything in sync
  const [display, setDisplay] = useState<DisplayState | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const tooltipRef = useRef<HTMLDivElement>(null);

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const currentStepIndex = display?.stepIndex ?? 0;
  const currentStep = display?.step ?? steps[0];
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === steps.length - 1;

  // Calculate position for a specific step
  const calculatePosition = useCallback((step: SpotlightStep): { targetRect: DOMRect | null; tooltipStyle: React.CSSProperties } => {
    const target = document.querySelector(step.target);
    if (!target || !tooltipRef.current) {
      return { targetRect: null, tooltipStyle: {} };
    }

    const rect = target.getBoundingClientRect();
    const tooltip = tooltipRef.current;
    const tooltipRect = tooltip.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Determine best placement
    let bestPlacement = step.placement || 'auto';

    if (bestPlacement === 'auto') {
      const spaceBottom = vh - rect.bottom;
      const spaceRight = vw - rect.right;
      const spaceTop = rect.top;

      if (spaceBottom >= tooltipRect.height + TOOLTIP_MARGIN) {
        bestPlacement = 'bottom';
      } else if (spaceRight >= tooltipRect.width + TOOLTIP_MARGIN) {
        bestPlacement = 'right';
      } else if (spaceTop >= tooltipRect.height + TOOLTIP_MARGIN) {
        bestPlacement = 'top';
      } else {
        bestPlacement = 'left';
      }
    }

    let top = 0;
    let left = 0;

    switch (bestPlacement) {
      case 'bottom':
        top = rect.bottom + TOOLTIP_MARGIN;
        left = rect.left + rect.width / 2 - tooltipRect.width / 2;
        break;
      case 'top':
        top = rect.top - tooltipRect.height - TOOLTIP_MARGIN;
        left = rect.left + rect.width / 2 - tooltipRect.width / 2;
        break;
      case 'right':
        top = rect.top + rect.height / 2 - tooltipRect.height / 2;
        left = rect.right + TOOLTIP_MARGIN;
        break;
      case 'left':
        top = rect.top + rect.height / 2 - tooltipRect.height / 2;
        left = rect.left - tooltipRect.width - TOOLTIP_MARGIN;
        break;
    }

    // Keep within viewport
    left = Math.max(16, Math.min(left, vw - tooltipRect.width - 16));
    top = Math.max(16, Math.min(top, vh - tooltipRect.height - 16));

    return { targetRect: rect, tooltipStyle: { top, left } };
  }, []);

  // Wait for element to appear
  const waitForElement = useCallback((selector: string, timeout = 2000): Promise<Element | null> => {
    return new Promise((resolve) => {
      const el = document.querySelector(selector);
      if (el) {
        resolve(el);
        return;
      }

      const observer = new MutationObserver(() => {
        const found = document.querySelector(selector);
        if (found) {
          observer.disconnect();
          resolve(found);
        }
      });

      observer.observe(document.body, { childList: true, subtree: true });

      setTimeout(() => {
        observer.disconnect();
        resolve(document.querySelector(selector));
      }, timeout);
    });
  }, []);

  // Show a specific step
  const showStep = useCallback(async (stepIndex: number) => {
    const step = steps[stepIndex];
    if (!step) return;

    setIsTransitioning(true);

    // Navigate only if route is specified and different from current
    if (step.route && location.pathname !== step.route) {
      navigate(step.route);
      await new Promise(r => setTimeout(r, 500));
    }

    // Wait for target element
    const targetSelector = step.waitFor || step.target;
    await waitForElement(targetSelector, 2000);

    // Extra delay for DOM stability
    await new Promise(r => setTimeout(r, 150));

    step.onBeforeShow?.();

    // Calculate position and update display state together
    requestAnimationFrame(() => {
      const { targetRect, tooltipStyle } = calculatePosition(step);
      setDisplay({
        stepIndex,
        step,
        targetRect,
        tooltipStyle,
      });
      setIsTransitioning(false);
      step.onAfterShow?.();
    });
  }, [steps, location.pathname, navigate, waitForElement, calculatePosition]);

  // Update position on scroll/resize
  const updatePosition = useCallback(() => {
    if (!display) return;
    const { targetRect, tooltipStyle } = calculatePosition(display.step);
    setDisplay(prev => prev ? { ...prev, targetRect, tooltipStyle } : null);
  }, [display, calculatePosition]);

  // Initialize
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      showStep(startAtStep);
    } else {
      document.body.style.overflow = '';
      setDisplay(null);
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Update on resize/scroll
  useEffect(() => {
    if (!isOpen || !display) return;

    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen, display, updatePosition]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight' && !isLastStep && !isTransitioning) showStep(currentStepIndex + 1);
      else if (e.key === 'ArrowLeft' && !isFirstStep && !isTransitioning) showStep(currentStepIndex - 1);
    };

    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, isFirstStep, isLastStep, isTransitioning, currentStepIndex, showStep, onClose]);

  // Don't render on mobile - tour requires sidebar to be visible
  if (!isOpen || isMobile) return null;

  const targetRect = display?.targetRect;
  const tooltipStyle = display?.tooltipStyle || {};
  const isReady = display !== null && targetRect !== null && !isTransitioning;

  // SVG spotlight path
  const spotlightPath = targetRect ? (() => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    const x = targetRect.left - PADDING;
    const y = targetRect.top - PADDING;
    const rw = targetRect.width + PADDING * 2;
    const rh = targetRect.height + PADDING * 2;
    const r = 12;

    return `M0,0 L${w},0 L${w},${h} L0,${h} Z
            M${x + r},${y}
            L${x + rw - r},${y} Q${x + rw},${y} ${x + rw},${y + r}
            L${x + rw},${y + rh - r} Q${x + rw},${y + rh} ${x + rw - r},${y + rh}
            L${x + r},${y + rh} Q${x},${y + rh} ${x},${y + rh - r}
            L${x},${y + r} Q${x},${y} ${x + r},${y} Z`;
  })() : `M0,0 L${window.innerWidth},0 L${window.innerWidth},${window.innerHeight} L0,${window.innerHeight} Z`;

  return createPortal(
    <div className="fixed inset-0 z-[9999]" style={{ pointerEvents: 'auto' }}>
      {/* Dark overlay with spotlight hole */}
      <svg className="absolute inset-0 w-full h-full" style={{ pointerEvents: 'none' }}>
        <defs>
          <linearGradient id="glow-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#818cf8" />
            <stop offset="100%" stopColor="#c084fc" />
          </linearGradient>
        </defs>
        <path
          d={spotlightPath}
          fill="rgba(0, 0, 0, 0.8)"
          fillRule="evenodd"
          style={{ transition: 'all 0.3s ease-out' }}
        />
        {targetRect && isReady && (
          <rect
            x={targetRect.left - PADDING - 2}
            y={targetRect.top - PADDING - 2}
            width={targetRect.width + PADDING * 2 + 4}
            height={targetRect.height + PADDING * 2 + 4}
            rx={14}
            fill="none"
            stroke="url(#glow-gradient)"
            strokeWidth="3"
            style={{ transition: 'all 0.3s ease-out' }}
          />
        )}
      </svg>

      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-20 p-2.5 bg-white/10 hover:bg-white/20 backdrop-blur-md rounded-full text-white transition-all"
      >
        <XMarkIcon className="w-5 h-5" />
      </button>

      {/* Step indicator */}
      <div className="absolute top-4 left-4 z-20 flex items-center gap-2 px-3 py-1.5 bg-white/10 backdrop-blur-md rounded-full">
        <span className="text-white/90 text-sm font-medium">
          {currentStepIndex + 1} / {steps.length}
        </span>
      </div>

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className="absolute z-20 w-[340px] max-w-[calc(100vw-32px)]"
        style={{
          ...tooltipStyle,
          opacity: isReady ? 1 : 0,
          transform: isReady ? 'translateY(0)' : 'translateY(8px)',
          transition: 'opacity 0.2s ease-out, transform 0.2s ease-out',
          pointerEvents: 'auto',
        }}
      >
        <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl shadow-2xl border border-white/10 overflow-hidden">
          {/* Gradient top bar */}
          <div className="h-1.5 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500" />

          <div className="p-5">
            {/* Icon and Title */}
            <div className="flex items-start gap-3 mb-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                <SparklesIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white leading-tight">
                  {t(currentStep.title)}
                </h3>
                <p className="text-xs text-indigo-300 mt-0.5">
                  {t('tour.stepOf', { current: currentStepIndex + 1, total: steps.length })}
                </p>
              </div>
            </div>

            {/* Description */}
            <p className="text-gray-300 text-sm leading-relaxed mb-5">
              {t(currentStep.description)}
            </p>

            {/* Progress bar */}
            <div className="h-1 bg-gray-700 rounded-full mb-5 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full transition-all duration-300"
                style={{ width: `${((currentStepIndex + 1) / steps.length) * 100}%` }}
              />
            </div>

            {/* Navigation buttons */}
            <div className="flex items-center justify-between">
              <button
                onClick={() => !isFirstStep && !isTransitioning && showStep(currentStepIndex - 1)}
                disabled={isFirstStep || isTransitioning}
                className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                  isFirstStep || isTransitioning
                    ? 'text-gray-600 cursor-not-allowed'
                    : 'text-gray-400 hover:text-white hover:bg-white/10'
                }`}
              >
                <ChevronLeftIcon className="w-4 h-4" />
                {t('tour.previous')}
              </button>

              {isLastStep ? (
                <button
                  onClick={onClose}
                  className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white text-sm font-semibold rounded-xl shadow-lg shadow-indigo-500/25 transition-all"
                >
                  <SparklesIcon className="w-4 h-4" />
                  {t('tour.finish')}
                </button>
              ) : (
                <button
                  onClick={() => !isTransitioning && showStep(currentStepIndex + 1)}
                  disabled={isTransitioning}
                  className="flex items-center gap-1 px-4 py-2.5 bg-white/10 hover:bg-white/20 text-white text-sm font-medium rounded-xl transition-all"
                >
                  {t('tour.next')}
                  <ChevronRightIcon className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Keyboard hint */}
          <div className="px-5 py-2.5 bg-black/20 border-t border-white/5">
            <p className="text-xs text-gray-500 text-center">
              {t('tour.keyboardHint')}
            </p>
          </div>
        </div>
      </div>

      {/* Loading overlay */}
      {isTransitioning && (
        <div className="absolute inset-0 flex items-center justify-center z-30 bg-black/50">
          <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 border-3 border-indigo-500 border-t-transparent rounded-full animate-spin" />
            <p className="text-white/80 text-sm">{t('tour.loading')}</p>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}
